import React, { useState, useEffect, useCallback } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { Umbrella, Edit2, AlertTriangle, Info } from 'lucide-react';
import * as payrollApi from '../../../../services/api/payrollApi';
import UpdateVacationBalanceModal from './UpdateVacationBalanceModal';

/**
 * VacationBalanceCard — per-employee semester saldo on the employee profile.
 *
 * Read-only view with a "Redigera" button that opens UpdateVacationBalanceModal
 * for admin-set field edits (earned / carried / saved). Matches the pattern
 * used by CompensationCard + TaxProfileCard on the same page.
 *
 * Backend: GET /payroll/vacation-balance?employeeId=... (load)
 *          PUT /payroll/vacation-balance (save, from the modal)
 */
const VacationBalanceCard = ({ employeeId, companyId }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await payrollApi.getVacationBalance(companyId, { employeeId });
      setSnapshot(res.data?.snapshot || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda semestersaldo.');
    } finally {
      setLoading(false);
    }
  }, [companyId, employeeId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="info-section mb-4">
        <div className="p-4 text-center">
          <Spinner animation="border" size="sm" />
          <span className="ms-2 text-muted">Laddar semestersaldo…</span>
        </div>
      </div>
    );
  }

  const hasAdvance = (snapshot?.advanceDaysTaken || 0) > 0;

  return (
    <>
      <div className="info-section mb-4" role="region" aria-labelledby="vacation-balance-heading">
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Umbrella size={20} className="me-3 text-info" aria-hidden="true" />
              <div>
                <h5 className="mb-0 fw-bold" id="vacation-balance-heading">Semestersaldo</h5>
                <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                  {snapshot
                    ? `Semesterår ${snapshot.year}/${snapshot.year + 1} (${snapshot.dateRange.from} – ${snapshot.dateRange.to})`
                    : 'Ingen data för nuvarande semesterår'}
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              className="action-button d-flex align-items-center"
              onClick={() => setShowModal(true)}
              disabled={!snapshot}
              aria-label="Redigera semestersaldo"
            >
              <Edit2 size={16} className="me-2" aria-hidden="true" />
              Redigera
            </Button>
          </div>

          <div className="section-divider"></div>

          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

          {snapshot && (
            <>
              <div className="row g-3 mb-3">
                <StatBox label="Tjänat"     value={snapshot.earnedDays}        hint="Intjänade dagar under föregående intjänandeår (§4+§7)" />
                <StatBox label="Överförda"  value={snapshot.carriedOverDays}   hint="Överförda från tidigare år" />
                <StatBox label="Sparat"     value={snapshot.savedDays}         hint="Sparade dagar (§18, max 5 per år i ackumulering)" />
                <StatBox label="Använd"     value={snapshot.usedDays}          hint="Beräknas från godkända SEMESTER-poster på lönespecar" />
                <StatBox label="Kvar"       value={snapshot.remainingPaidDays} emphasis={snapshot.remainingPaidDays > 0 ? 'success' : 'muted'} hint="max(0, Tjänat + Överförda − Använd)" />
                <StatBox label="Förskott"   value={snapshot.advanceDaysTaken}  emphasis={hasAdvance ? 'warning' : 'muted'} hint="Använd över det betalda taket — tillåts men bokförs som förskott" />
              </div>

              {hasAdvance && (
                <Alert variant="warning" className="d-flex align-items-start gap-2 mb-0">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-1" />
                  <div style={{ fontSize: 13 }}>
                    Anställd har tagit ut {snapshot.advanceDaysTaken} dagar mer än det betalda taket —
                    hanteras som förskott. Semesterersättningen behöver justeras vid slutlön.
                  </div>
                </Alert>
              )}

              <div className="d-flex align-items-start gap-2 mt-3" style={{ fontSize: 12, color: '#6b7280' }}>
                <Info size={14} className="flex-shrink-0 mt-1" />
                <div>
                  <strong>Tjänat</strong>, <strong>Överförda</strong> och <strong>Sparat</strong> sätts
                  manuellt av lönehandläggare (eller via bulk-intjäning §7). <strong>Använd</strong> uppdateras
                  automatiskt när semesterlöner bokförs på lönespec.
                </div>
              </div>
            </>
          )}

          {!snapshot && !error && (
            <p className="text-muted mt-2 mb-0">
              Kör "Intjäna §7 (alla)" från Lön → Semester för att initialisera saldot, eller ange värden manuellt via Redigera.
            </p>
          )}
        </div>
      </div>

      <UpdateVacationBalanceModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        employeeId={employeeId}
        companyId={companyId}
        snapshot={snapshot}
        onSuccess={load}
      />
    </>
  );
};

const StatBox = ({ label, value, emphasis, hint }) => {
  const color = emphasis === 'success' ? 'text-success'
    : emphasis === 'warning' ? 'text-warning'
    : emphasis === 'muted' ? 'text-muted'
    : '';
  return (
    <div className="col-md-4 col-lg-2">
      <div className="p-3 border rounded-lg h-100">
        <div className="text-muted small mb-1" title={hint}>{label}</div>
        <div className={`h5 mb-0 fw-bold ${color}`}>{Number(value || 0)}</div>
      </div>
    </div>
  );
};

export default VacationBalanceCard;
